import { useCallback, useEffect, useState } from 'react'
import Sprint, { SprintStatus } from '../../../../lib/functions/sprint'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

// SprintReportNew のスプリント選択プルダウン専用のグループ表示順。
// プルダウンでは FINISHED と CANCELED を「FINISHED & CANCELED」1グループに統合するため、
// この2つは同じソートインデックスにし、グループ内は status を区別せず開始日順に並べる。
// 折りたたみグループ (= 統合グループ) を先頭に置き、続けて INPROGRESS → STANDBY。
const SPRINT_SELECT_GROUP_SORT_INDEX: Record<SprintStatus, number> = {
  [SprintStatus.FINISHED]: 0,
  [SprintStatus.CANCELED]: 0,
  [SprintStatus.INPROGRESS]: 1,
  [SprintStatus.STANDBY]: 2,
}

export type SprintOption = {
  value: string
  name: string
  subText: string
  teamUuid: string
  status: SprintStatus
  startDate: string
  endDate: string
}

const formatSprintPeriod = (startDate: string, endDate: string): string => {
  const start = dateV2Service.fromString(startDate)
  const end = dateV2Service.fromString(endDate)
  if (!start || !end) return ''
  return `${dateV2Service.formatMonthAndDate(start)}〜${dateV2Service.formatMonthAndDate(end)}`
}

const getSprintGroupSortIndex = (status: SprintStatus): number =>
  SPRINT_SELECT_GROUP_SORT_INDEX[status]

// プルダウンの並べ替えルール (上記グループ順 → 同一グループ内は startDate 昇順)。
export const useSprintOptions = (): {
  sprintOptions: SprintOption[]
  loaded: boolean
} => {
  const [sprintOptions, setSprintOptions] = useState<SprintOption[]>([])
  const [loaded, setLoaded] = useState(false)

  const fetchSprintOptions = useCallback(async () => {
    const response = await Sprint.getSprints({ statusList: [] })
    if (!response.json) {
      setLoaded(true)
      return
    }
    // response.json を直接 sort すると元の配列を破壊するため、複製してから並べ替える。
    const options: SprintOption[] = [...response.json]
      .sort((a, b) => {
        const groupDiff =
          getSprintGroupSortIndex(a.status) - getSprintGroupSortIndex(b.status)
        if (groupDiff !== 0) return groupDiff
        // 同じグループ内では開始日の昇順（古いものが上）。FINISHED/CANCELED は
        // 統合グループなので status を区別せず、純粋に時系列で並べる。
        return a.startDate.localeCompare(b.startDate)
      })
      .map(sprint => ({
        value: sprint.uuid,
        name: sprint.name,
        subText: formatSprintPeriod(sprint.startDate, sprint.endDate),
        teamUuid: sprint.teamUuid,
        status: sprint.status,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
      }))
    setSprintOptions(options)
    setLoaded(true)
  }, [])

  useEffect(() => {
    fetchSprintOptions()
  }, [fetchSprintOptions])

  return { sprintOptions, loaded }
}
